"use client";

import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

/* ================= DATA ================= */

const CATEGORIES = [
  {
    id: 1,
    title: "Playful Toys",
    subtitle: "Spark imagination",
    slug: "toys",
    image:
      "https://oextech.blr1.digitaloceanspaces.com/OexTech/kiddokart%40aicodepro.com/Kiddo%20Kart/kiddokart/stackers-toys-min-768x427.jpg",
    className: "md:col-span-1",
  },
  {
    id: 2,
    title: "Trendy Clothing",
    subtitle: "Comfort meets style",
    slug: "clothing",
    image:
      "https://oextech.blr1.digitaloceanspaces.com/OexTech/kiddokart%40aicodepro.com/Kiddo%20Kart/kiddokart/Gemini_Generated_Image_a1xhv2a1xhv2a1xh.png",
    className: "md:col-span-2",
  },
  {
    id: 3,
    title: "Bedtime Stories",
    subtitle: "Books for dreamers",
    slug: "books",
    image:
      "https://oextech.blr1.digitaloceanspaces.com/OexTech/kiddokart%40aicodepro.com/Kiddo%20Kart/kiddokart/Gemini_Generated_Image_m00e69m00e69m00e.png",
    className: "md:col-span-1",
  },
  {
    id: 4,
    title: "Accessories",
    subtitle: "The finishing touch",
    slug: "accessories",
    image:
      "https://oextech.blr1.digitaloceanspaces.com/OexTech/kiddokart%40aicodepro.com/Kiddo%20Kart/kiddokart/Gemini_Generated_Image_8zo4k18zo4k18zo4.png",
    className: "md:col-span-2",
  },
  {
    id: 5,
    title: "Nursery",
    subtitle: "Cozy spaces",
    slug: "nursery",
    image:
      "https://oextech.blr1.digitaloceanspaces.com/OexTech/kiddokart%40aicodepro.com/Kiddo%20Kart/kiddokart/Gemini_Generated_Image_r7yiufr7yiufr7yi.png",
    className: "md:col-span-2",
  },
];

/* ================= COMPONENT ================= */

export default function AestheticCategory() {
  return (
    <section
      className="relative py-24 overflow-hidden"
      style={{ background: "var(--bg-col-1)" }}
    >
      {/* Background Decoration */}
      <div
        className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-[120px] -z-10"
        style={{ background: "var(--decorative-4)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full blur-[120px] -z-10"
        style={{ background: "var(--decorative-3)" }}
      />

      <div className="max-w-7xl mx-auto px-6">
        {/* ================= HEADER ================= */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span
            className="text-sm font-semibold tracking-widest uppercase mb-3 block"
            style={{ color: "var(--secondary)" }}
          >
            Collections
          </span>

          <h2
            className="text-4xl md:text-5xl font-bold tracking-tight mb-4"
            style={{ color: "var(--title)" }}
          >
            Curated for{" "}
            <span
              className="italic   font-light"
              style={{ color: "var(--text-muted)" }}
            >
              Little Ones
            </span>
          </h2>
        </motion.div>

        {/* ================= BENTO GRID ================= */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-[300px]">
          {CATEGORIES.map((cat, index) => (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className={`relative group rounded-3xl overflow-hidden ${cat.className}`}
            >
              <Link
                href={`/category/${cat.slug}`}
                className="block w-full h-full"
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <Image
                    src={cat.image}
                    alt={cat.title}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>

                {/* Overlay */}
                <div
                  className="absolute inset-0 transition-opacity duration-300"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.25), transparent)",
                    opacity: 0.65,
                  }}
                />

                {/* Content */}
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  {/* Action Button */}
                  <div
                    className="
                      absolute top-6 right-6 w-10 h-10
                      rounded-full flex items-center justify-center
                      opacity-0 translate-y-4
                      group-hover:opacity-100 group-hover:translate-y-0
                      transition-all duration-300
                    "
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255,255,255,0.3)",
                    }}
                  >
                    <ArrowUpRight className="w-5 h-5 text-white" />
                  </div>

                  {/* Text */}
                  <div className="transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-xs font-medium tracking-wider uppercase mb-1 text-white/70">
                      {cat.subtitle}
                    </p>
                    <h3 className="text-2xl font-bold tracking-tight text-white">
                      {cat.title}
                    </h3>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
